import { Button, Result } from 'antd';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

/** Keeps a crashing page from blanking the whole app; key it on the route so navigating away resets it. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page crashed', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    // A failed lazy chunk after a deploy is fixed by a reload; anything else, retrying the render may be enough.
    const chunk = /dynamically imported module|Loading chunk|Importing a module script failed/i.test(this.state.error.message);
    return (
      <Result
        status="error"
        title="Something went wrong on this page"
        subTitle={chunk ? 'A new version of the dashboard is available.' : this.state.error.message}
        extra={[
          <Button key="reload" type="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>,
          !chunk && (
            <Button key="retry" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          ),
        ]}
      />
    );
  }
}
